import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import SuccessFace from 'components/ui/AnimatedStates/SuccessFace';
import ErrorFace from 'components/ui/AnimatedStates/ErrorFace';
import LoadingFace from 'components/ui/AnimatedStates/LoadingFace';

import { getTranslatedString } from 'helpers/i18n';
import NoticeForm from './NoticeForm/NoticeForm';

import { editNotice, deleteNotice } from './store/actions';
import { downloadTargetedPdf } from 'actions/landlordNoticesActions';
import WarningFace from 'components/ui/AnimatedStates/WarningFace';
import DrawerTabs from 'components/ui/DrawerTabs';
import NoticeDeliveryReport from '.././NoticeDeliveryReport';
import { media } from 'helpers/MediaHelper';

const BodyContent = styled.div`
  padding: 24px 24px 35px;
  overflow-y: scroll;
  height: inherit;

  ${media.mobile`
    padding: 24px 24px 80px;
  `};
`;

const StyledTabPane = styled(DrawerTabs.TabPane)`
  height: 100% !important;
  padding-bottom: 50px;
`;

const EDITING_STATES = {
  form: 'form',
  deleting: 'deleting',
  loading: 'loading',
  successDeleting: 'successDeleting',
  error: 'error',
  errorDeleting: 'errorDeleting',
};

const FORM_NAME = 'edit_notice_form';

const EDIT_TAB = '1';
const DELIVERY_REPORT_TAB = '2';

const PARTIALLY_DELIVERED = 'Partially Delivered';
const DELIVERED = 'Delivered';
const EXPIRED = 'Expired';

const EditNotice = ({
  noticeDetails,
  deliveryReport,
  handleClose,
  onSubmissionComplete,
  editNotice,
  deleteNotice,
  downloadTargetedPdf,
}) => {
  const [tabIndex, setTabIndex] = useState(EDIT_TAB);
  const [editingState, setEditingState] = useState(EDITING_STATES.form);
  const [tempValues, setTempValues] = useState({});

  const changeTab = (value) => {
    setTabIndex(value);
  };

  const handleEditNotice = useCallback(
    async (values) => {
      setEditingState(EDITING_STATES.loading);
      try {
        // if the request fails, store the values temporarily to retry
        setTempValues(values);
        await editNotice(values, noticeDetails.id);
        setTempValues({});
        setEditingState(EDITING_STATES.success);
        if (onSubmissionComplete) {
          onSubmissionComplete();
        }
      } catch (err) {
        setEditingState(EDITING_STATES.error);
      }
    },
    [editNotice, onSubmissionComplete, noticeDetails]
  );

  const handleDeleteNotice = useCallback(async () => {
    setEditingState(EDITING_STATES.loading);
    try {
      await deleteNotice(noticeDetails.id);
      setEditingState(EDITING_STATES.successDeleting);
      if (onSubmissionComplete) {
        onSubmissionComplete();
      }
    } catch (err) {
      setEditingState(EDITING_STATES.errorDeleting);
    }
  }, [deleteNotice, onSubmissionComplete, noticeDetails]);

  const handleRetry = useCallback(() => {
    handleEditNotice(tempValues);
  }, [handleEditNotice, tempValues]);

  if (editingState === EDITING_STATES.form) {
    return (
      <FormFace
        tabIndex={tabIndex}
        changeTab={changeTab}
        handleClose={handleClose}
        handleEditNotice={handleEditNotice}
        noticeDetails={noticeDetails}
        deliveryReport={deliveryReport}
        setEditingState={setEditingState}
        downloadTargetedPdf={downloadTargetedPdf}
      />
    );
  } else if (editingState === EDITING_STATES.deleting) {
    return (
      <WarningFace
        headerText={getTranslatedString('landlordPortal.noticeDetailsOverview.areYouSure')}
        bodyText={getTranslatedString(
          'landlordPortal.noticeDetailsOverview.areYouSureCannotBeUndoneDeactivate'
        )}
        onClick={handleDeleteNotice}
        buttonText={getTranslatedString('landlordPortal.noticeDetailsOverview.deactivate')}
        onSecondaryClick={() => setEditingState(EDITING_STATES.form)}
        secondaryButtonText={getTranslatedString('cancel')}
      />
    );
  } else if (editingState === EDITING_STATES.loading) {
    return (
      <LoadingFace
        headerText={getTranslatedString('landlordPortal.notices.createLoading')}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  } else if (
    editingState === EDITING_STATES.success ||
    editingState === EDITING_STATES.successDeleting
  ) {
    return (
      <SuccessFace
        onClick={handleClose}
        headerText={getTranslatedString('landlordPortal.notices.createSuccess')}
        bodyText={getTranslatedString('globalDrawer.successBody')}
        buttonText={getTranslatedString('close')}
      />
    );
  } else if (
    editingState === EDITING_STATES.error ||
    editingState === EDITING_STATES.errorDeleting
  ) {
    return (
      <ErrorFace
        headerText={getTranslatedString('landlordPortal.notices.createError')}
        bodyText={getTranslatedString('globalDrawer.errorBody')}
        onClick={() => setEditingState(EDITING_STATES.form)}
        buttonText={getTranslatedString('close')}
        onSecondaryClick={handleRetry}
        secondaryButtonText={getTranslatedString('globalDrawer.goBack')}
      />
    );
  }
};

const FormFace = ({
  handleClose,
  handleEditNotice,
  noticeDetails,
  deliveryReport,
  setEditingState,
  changeTab,
  tabIndex,
  downloadTargetedPdf,
}) => {
  const showDeliveryReport = [DELIVERED, PARTIALLY_DELIVERED, EXPIRED].includes(
    noticeDetails.status
  );
  return (
    <React.Fragment>
      <DrawerHeader
        noBorder
        onClose={handleClose}
        title={noticeDetails.name}
        subHeader={getTranslatedString('landlordPortal.notices.requiredField')}
      />
      <DrawerBody tabbedDrawerBodyStyle>
        <DrawerTabs defaultActiveKey={tabIndex} onChange={changeTab} color="white">
          <StyledTabPane
            tab={getTranslatedString('landlordPortal.noticeDetailsOverview.details')}
            key={EDIT_TAB}
          >
            <BodyContent>
              <NoticeForm
                mode="edit"
                formName={FORM_NAME}
                formData={noticeDetails}
                handleSubmit={handleEditNotice}
              />
            </BodyContent>
          </StyledTabPane>
          {showDeliveryReport && (
            <StyledTabPane
              tab={getTranslatedString('landlordPortal.noticeDetailsOverview.deliveryReport')}
              key={DELIVERY_REPORT_TAB}
            >
              <NoticeDeliveryReport
                properties={noticeDetails.properties || []}
                report={deliveryReport}
                downloadTargetedPdf={(propertyIds) =>
                  downloadTargetedPdf(noticeDetails.id, propertyIds)
                }
              />
            </StyledTabPane>
          )}
        </DrawerTabs>
      </DrawerBody>
      <DrawerFooter
        primaryActionFormName={FORM_NAME}
        secondaryActionText={getTranslatedString('landlordPortal.noticeDetailsOverview.deactivate')}
        primaryActionText={tabIndex === EDIT_TAB && getTranslatedString('save')}
        onSecondaryActionClick={() => setEditingState(EDITING_STATES.deleting)}
      />
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({
  noticeDetails: store.landlordNotices.noticeDetails,
  deliveryReport: store.landlordNotices.delivery_report,
});

const mapDispatchToProps = {
  editNotice,
  deleteNotice,
  downloadTargetedPdf,
};

export default connect(mapStoreToProps, mapDispatchToProps)(EditNotice);
